/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

export interface ProductListingHeaderProps {
    /**
     * Optional eyebrow line above the title. NM's search results page renders
     * "Search Results for" above the query; the PLP renders no eyebrow.
     */
    eyebrow?: string;
    /** Page title — category name on the PLP, search term on the SRP. */
    title: string;
    /**
     * Alternate title below `md`, when live words the same heading differently
     * at mobile. Live NM's SRP does: desktop reads `"Dress" results`
     * (.reference/captures/search-d-1280/App.tsx:410) and mobile reads
     * `Results for "Dress"` (search-m-390/App.tsx:212-213).
     *
     * OMITTED IS THE DEFAULT AND RENDERS THE OLD DOM EXACTLY — a bare string
     * child of the h1, no wrapper span. The PLP and BG pass nothing, so this
     * cannot move them.
     *
     * Both strings are in the markup and CSS picks one, rather than a viewport
     * hook choosing at runtime: the heading is above the fold and server-
     * rendered, so a client-measured swap would either flash the wrong copy or
     * shift layout. `display: none` content is excluded from the accessibility
     * tree, so the h1's accessible name is still exactly one of the two.
     */
    titleMobile?: string;
    /** Total number of matching products, rendered as a separate count line. */
    total: number;
    /**
     * Render the count line (default). The NM SRP passes false — blast-4 puts
     * the item count in the sort row ("Sort By: … | 10,985 items | 1 of 184"),
     * not under the results line; PLP/BG keep the count line here.
     */
    showCount?: boolean;
    /** Sort control slot — rendered on the trailing edge at desktop. */
    children?: ReactNode;
    className?: string;
}

/**
 * Shared results header for the PLP and search results surfaces.
 *
 * NM renders the page title and the result count as two separate elements
 * (legacy `CategoryTitle` + `ResultCount`), with the sort dropdown on the
 * trailing edge of the same row. Both listing routes use this component so the
 * two surfaces cannot drift apart.
 */
export default function ProductListingHeader({
    eyebrow,
    title,
    titleMobile,
    total,
    showCount = true,
    children,
    className,
}: ProductListingHeaderProps): ReactElement {
    const { t } = useTranslation('category');

    return (
        <div
            data-testid="product-listing-header"
            className={cn('mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between', className)}>
            <div className="min-w-0">
                {eyebrow && (
                    <p
                        data-testid="product-listing-eyebrow"
                        className="type-label-medium uppercase text-muted-foreground">
                        {eyebrow}
                    </p>
                )}
                <h1 data-testid="product-listing-title" className="type-title text-foreground">
                    {titleMobile === undefined ? (
                        title
                    ) : (
                        <>
                            <span data-testid="product-listing-title-mobile" className="md:hidden">
                                {titleMobile}
                            </span>
                            <span data-testid="product-listing-title-desktop" className="hidden md:inline">
                                {title}
                            </span>
                        </>
                    )}
                </h1>
                {showCount && (
                    <p data-testid="product-listing-count" className="type-label-medium text-muted-foreground">
                        {t('resultsCount', {
                            count: total,
                            defaultValue: '{{count}} items',
                            defaultValue_one: '{{count}} item',
                            defaultValue_other: '{{count}} items',
                        })}
                    </p>
                )}
            </div>
            {children && <div className="shrink-0">{children}</div>}
        </div>
    );
}
